// リアルタイムタブ・生徒タイムラインの統合タイムラインエントリ。
// AI 質問統合表示の凍結（005-freeze-ai-question-timeline）に伴い、学習記録単一ソースに縮約。
// 凍結解除時は AI 質問エントリを discriminated union として追加する（proposal「復帰時」）。
//
// マージ・ソート・ページングはフロント責務（フロント側でマージ）。
// 学習記録の具体型はソース（タイムライン API レスポンス）に依存するため、ドメインは
// 「日時を持つ」最小制約のジェネリックとして扱い、消費側が具体型 S を渡す。
// これによりドメイン層は特定の API 型・コンポーネント型に依存しない。

// マージ・ソートに必要な学習記録の最小形状
export type StudyRecordLike = {
  recordedAt: string;
};

export type MergedTimelineEntry<S extends StudyRecordLike = StudyRecordLike> = {
  kind: "study_record";
  // ソート用に正規化した比較可能な日時値（epoch ms）。
  sortedAt: number;
  studyRecord: S;
};

// ISO 日時文字列 → 比較可能な epoch ms。タイムゾーン差異はここで吸収する。
const toEpoch = (iso: string): number => new Date(iso).getTime();

const toMergedStudyRecord = <S extends StudyRecordLike>(
  studyRecord: S,
): MergedTimelineEntry<S> => ({
  kind: "study_record",
  sortedAt: toEpoch(studyRecord.recordedAt),
  studyRecord,
});

// 比較関数: 日時降順（新しいものが上）。同着は Array.prototype.sort の安定性で元配列順を保つ。
const compareMergedEntries = <S extends StudyRecordLike>(
  a: MergedTimelineEntry<S>,
  b: MergedTimelineEntry<S>,
): number => b.sortedAt - a.sortedAt;

// 学習記録配列を日時降順にソートする。
// 入力は元ソース内で降順整列済みを前提とし、安定ソートで同着の元配列順を保つ。
const mergeTimelineEntries = <S extends StudyRecordLike>(
  studyRecords: S[],
): MergedTimelineEntry<S>[] =>
  studyRecords
    .map((studyRecord) => toMergedStudyRecord(studyRecord))
    .sort(compareMergedEntries);

type RealtimeStreamInput<S extends StudyRecordLike> = {
  // 取得済みの学習記録（全ページ・降順）
  studyRecords: S[];
  // 次ページが存在するか
  hasNextStudyRecord: boolean;
  // 1 ページの件数（実運用 20）
  pageSize: number;
  // ユーザーが要求している表示ページ数（無限スクロールで増える。1 以上）
  requestedPages: number;
};

type RealtimeStreamResult<S extends StudyRecordLike> = {
  // 表示確定済みストリーム（日時降順・ページ跨ぎでも順序が崩れない安全な範囲）
  displayed: MergedTimelineEntry<S>[];
  // 要求ページ数を満たすために次ページ取得が必要か
  needFetchStudyRecord: boolean;
};

// バッファマージ・ページング。
// クライアント側バッファとしてマージしつつ、要求ページ数までを表示確定する。
// 単一ソースなのでバッファ不足による確定停止は起きないが、次ページ取得のトリガー通知は残す。
export const computeRealtimeStream = <S extends StudyRecordLike>(
  input: RealtimeStreamInput<S>,
): RealtimeStreamResult<S> => {
  const { studyRecords, hasNextStudyRecord, pageSize, requestedPages } = input;

  const buffer = mergeTimelineEntries(studyRecords);
  const capacity = requestedPages * pageSize;
  const displayed = buffer.slice(0, capacity);
  // 要求ページ数を満たすためにさらに次ページが必要か
  const needFetchStudyRecord =
    hasNextStudyRecord && buffer.length < capacity + pageSize;

  return { displayed, needFetchStudyRecord };
};
